import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import './Suppliers.css';

const API = import.meta.env.VITE_API_URL;
const DEFAULT_PAGE_SIZE = 50;

const ACRONYMS = new Set([
  'ATC','KNMP','SGP','DDD','HPK','PRK','ZI','AIP','GVS','RVG',
  'MAH','UDH','WTG','GPK','TDV','CU','PPP','DOT','LIP','MAT','EMEA','RVH',
]);

function humanizeColumn(col) {
  return col.split('_')
    .map(w => ACRONYMS.has(w.toUpperCase()) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

async function getToken(instance) {
  try {
    const accounts = instance.getAllAccounts();
    if (!accounts.length) return null;
    const resp = await instance.acquireTokenSilent({
      scopes: [`${import.meta.env.VITE_AZURE_CLIENT_ID}/.default`],
      account: accounts[0],
    });
    return resp.idToken;
  } catch {
    const accounts = instance.getAllAccounts();
    return accounts[0]?.idToken || null;
  }
}

function SearchableSelect({ id, label, options, value, onChange, openId, onRequestOpen }) {
  const [search, setSearch] = useState('');
  const isOpen = openId === id;
  const filtered = search
    ? options.filter(o => o.toLowerCase().includes(search.toLowerCase()))
    : options;

  const handleToggle = () => {
    onRequestOpen(isOpen ? null : id);
    if (isOpen) setSearch('');
  };

  const handleSelect = (v) => {
    onChange(v);
    onRequestOpen(null);
    setSearch('');
  };

  return (
    <div style={{ position: 'relative', minWidth: 0, flex: '1 1 0' }}>
      <label style={{ fontSize: 11, color: 'var(--t3)', display: 'block', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</label>
      <div
        onClick={handleToggle}
        style={{
          padding: '5px 8px', fontSize: 12, border: '1px solid #d0d7de', borderRadius: 5,
          background: 'var(--card)', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden',
          textOverflow: 'ellipsis', color: value ? 'var(--t1)' : 'var(--t3)', minHeight: 28,
        }}
      >
        {value || 'All'}
      </div>
      {isOpen && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, minWidth: 180, zIndex: 100,
          background: 'var(--card)', border: '1px solid #d0d7de', borderRadius: 5,
          maxHeight: 220, overflow: 'hidden', display: 'flex', flexDirection: 'column',
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        }}>
          <input
            autoFocus
            type="text"
            placeholder="Type to search..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            onClick={e => e.stopPropagation()}
            style={{ padding: '5px 8px', fontSize: 12, border: 'none', borderBottom: '1px solid #eee', outline: 'none' }}
          />
          <div style={{ overflowY: 'auto', flex: 1 }}>
            <div
              onClick={() => handleSelect('')}
              style={{ padding: '4px 8px', fontSize: 12, cursor: 'pointer', color: 'var(--t3)', fontStyle: 'italic' }}
            >All</div>
            {filtered.slice(0, 200).map(o => (
              <div
                key={o}
                onClick={() => handleSelect(o)}
                style={{
                  padding: '4px 8px', fontSize: 12, cursor: 'pointer',
                  background: o === value ? '#eef3f7' : 'transparent',
                }}
              >{o}</div>
            ))}
            {filtered.length > 200 && (
              <div style={{ padding: '4px 8px', fontSize: 11, color: 'var(--t3)' }}>
                {filtered.length - 200} more — type to narrow
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const EMPTY_FILTERS = { genericName: '', dosageForm: '', strength: '', strengthUnit: '', supplier: '', outlet: '' };

export default function RetailHospYear() {
  const { year } = useParams();
  const { instance } = useMsal();
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ columns: [], rows: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterOptions, setFilterOptions] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [openFilterId, setOpenFilterId] = useState(null);
  const filterBarRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handler = (e) => {
      if (filterBarRef.current && !filterBarRef.current.contains(e.target)) {
        setOpenFilterId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Fetch filter options once per year
  useEffect(() => {
    (async () => {
      try {
        const token = await getToken(instance);
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await fetch(`${API}/api/market-intelligence/retail-hosp/${year}/filter-options`, { headers });
        if (res.ok) setFilterOptions(await res.json());
      } catch { /* ignore */ }
    })();
    setFilters(EMPTY_FILTERS);
    setPage(1);
  }, [year, instance]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken(instance);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = new URLSearchParams({ page, pageSize: DEFAULT_PAGE_SIZE });
      for (const [k, v] of Object.entries(filters)) {
        if (v) params.set(k, v);
      }
      const res = await fetch(`${API}/api/market-intelligence/retail-hosp/${year}?${params}`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData({ columns: json.columns || [], rows: json.rows || [], total: json.total || 0 });
    } catch (err) {
      setError(err.message);
      setData({ columns: [], rows: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [instance, year, page, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateFilter = (key, value) => {
    setFilters(f => ({ ...f, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
  };

  const hasActiveFilter = Object.values(filters).some(v => v);

  const pageCount = Math.max(1, Math.ceil(data.total / DEFAULT_PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageStart = (safePage - 1) * DEFAULT_PAGE_SIZE;
  const pageEnd = Math.min(safePage * DEFAULT_PAGE_SIZE, data.total);

  const paginationNums = (() => {
    const win = 5;
    let s = Math.max(1, safePage - Math.floor(win / 2));
    let e = Math.min(pageCount, s + win - 1);
    s = Math.max(1, e - win + 1);
    const nums = [];
    for (let i = s; i <= e; i++) nums.push(i);
    return nums;
  })();

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 22px', background: 'var(--surf)' }}>
      <h4 style={{ fontWeight: 700, marginBottom: 16, color: 'var(--t1)' }}>Retail Hosp {year}</h4>

      {/* Filter bar */}
      {filterOptions && (
        <div ref={filterBarRef} style={{ display: 'flex', gap: 6, flexWrap: 'nowrap', marginBottom: 12, alignItems: 'flex-end' }}>
          <SearchableSelect id="gn" label="Generic Molecule Name" options={filterOptions.genericName || []} value={filters.genericName} onChange={v => updateFilter('genericName', v)} openId={openFilterId} onRequestOpen={setOpenFilterId} />
          <SearchableSelect id="df" label="Dosage Form" options={filterOptions.dosageForm || []} value={filters.dosageForm} onChange={v => updateFilter('dosageForm', v)} openId={openFilterId} onRequestOpen={setOpenFilterId} />
          <SearchableSelect id="st" label="Strength" options={filterOptions.strength || []} value={filters.strength} onChange={v => updateFilter('strength', v)} openId={openFilterId} onRequestOpen={setOpenFilterId} />
          <SearchableSelect id="su" label="Strength Unit" options={filterOptions.strengthUnit || []} value={filters.strengthUnit} onChange={v => updateFilter('strengthUnit', v)} openId={openFilterId} onRequestOpen={setOpenFilterId} />
          <SearchableSelect id="sp" label="Supplier" options={filterOptions.supplier || []} value={filters.supplier} onChange={v => updateFilter('supplier', v)} openId={openFilterId} onRequestOpen={setOpenFilterId} />
          <SearchableSelect id="ol" label="Outlet" options={filterOptions.outlet || []} value={filters.outlet} onChange={v => updateFilter('outlet', v)} openId={openFilterId} onRequestOpen={setOpenFilterId} />
          {hasActiveFilter && (
            <button
              type="button"
              onClick={clearFilters}
              style={{
                padding: '5px 10px', fontSize: 11, fontWeight: 500, color: 'var(--teal)',
                background: 'var(--card)', border: '1px solid var(--teal)', borderRadius: 6,
                cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
              }}
            >Clear</button>
          )}
        </div>
      )}

      {error && (
        <div style={{ padding: 16, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, color: '#991b1b', fontSize: 13, marginBottom: 16 }}>
          Failed to load data: {error}
        </div>
      )}

      {loading && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>Loading...</div>
      )}

      {!loading && !error && (
        <div className="sup-table-wrap">
          <div className="sup-table-scroll" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '72vh', WebkitOverflowScrolling: 'touch' }}>
            <table className="sup-table" style={{ tableLayout: 'auto' }}>
              <thead>
                <tr>
                  {data.columns.map(col => (
                    <th key={col}>{humanizeColumn(col)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 ? (
                  <tr>
                    <td colSpan={data.columns.length || 1} style={{ padding: 32, textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>
                      No data available
                    </td>
                  </tr>
                ) : data.rows.map((row, i) => (
                  <tr key={i}>
                    {data.columns.map(col => (
                      <td key={col} style={{ whiteSpace: 'nowrap', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row[col] ?? ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'var(--card)', borderTop: '1px solid #eef3f7', borderBottomLeftRadius: 12, borderBottomRightRadius: 12, flexWrap: 'nowrap' }}>
            <nav aria-label="pagination" style={{ flexShrink: 0, display: 'flex', gap: 4, alignItems: 'center' }}>
              <button type="button" disabled={safePage === 1} onClick={() => setPage(p => Math.max(1, p - 1))} style={{ fontSize: 12, color: safePage === 1 ? 'var(--t3)' : 'var(--teal)', border: '1px solid ' + (safePage === 1 ? '#dee2e6' : 'var(--teal)'), background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: safePage === 1 ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>Previous</button>
              {paginationNums.map(n => (
                <button key={n} type="button" onClick={() => setPage(n)} style={{ fontSize: 12, color: safePage === n ? 'white' : 'var(--teal)', border: '1px solid var(--teal)', background: safePage === n ? 'var(--teal)' : 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: safePage === n ? 600 : 400 }}>{n}</button>
              ))}
              <button type="button" disabled={safePage === pageCount} onClick={() => setPage(p => Math.min(pageCount, p + 1))} style={{ fontSize: 12, color: safePage === pageCount ? 'var(--t3)' : 'var(--teal)', border: '1px solid ' + (safePage === pageCount ? '#dee2e6' : 'var(--teal)'), background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: safePage === pageCount ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>Next</button>
            </nav>
            <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--t3)', whiteSpace: 'nowrap' }}>
              Showing {data.total === 0 ? 0 : pageStart + 1}–{pageEnd} of {data.total.toLocaleString('en-US')} rows
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
